import React, { useState } from 'react';
import { Building2, UserPlus, Key, ArrowLeft, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { authAPI } from '../../lib/api';
import { apiErrorMessage } from '../../lib/utils';

const DEPARTMENTS = [
  'General Medicine', 'Surgery', 'Pediatrics', 'Gynecology', 'Orthopedics',
  'Cardiology', 'Neurology', 'Emergency', 'Radiology', 'Pathology',
  'Pharmacy', 'Laboratory', 'Imaging', 'Administration', 'ICU', 'General Ward', 'Front Desk'
];

export function InstitutionDetailModal({
  open, onOpenChange, institution, users, loadingUsers,
  onUsersRefresh
}) {
  const [addUserModal, setAddUserModal] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState({ open: false, user: null });
  const [userDetailView, setUserDetailView] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [newUserData, setNewUserData] = useState({
    full_name: '', email: '', password: '', role: 'doctor',
    department: '', phone: ''
  });

  const handleAddUser = async () => {
    if (!newUserData.full_name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill all required fields (Name, Email, Password)');
      return;
    }
    if (newUserData.password.length < 8) {
      toast.error('Password must be at least 8 characters with letters and numbers');
      return;
    }
    try {
      const userData = {
        ...newUserData,
        institution_id: institution?.id
      };
      await api.post('/auth/register', userData);
      toast.success(`User ${newUserData.full_name} created successfully`);
      setAddUserModal(false);
      setNewUserData({ full_name: '', email: '', password: '', role: 'doctor', department: '', phone: '' });
      onUsersRefresh?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters with letters and numbers');
      return;
    }
    try {
      await authAPI.adminChangePassword(changePasswordModal.user.id, { new_password: newPassword });
      toast.success('Password changed successfully');
      setChangePasswordModal({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to change password'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`User "${userName}" deleted successfully`);
      onUsersRefresh?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete user'));
    }
  };

  return (
    <>
      {/* Main Modal */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Building2 className="w-6 h-6 text-purple-600" />
              {institution?.institution_name} - User Management
            </DialogTitle>
            <DialogDescription>Manage all users for this institution</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-slate-500">Institution Code</p>
                <p className="font-mono text-sm">{institution?.institution_code}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <p className="text-sm">{institution?.subscription_status}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Users</p>
                <p className="text-sm font-bold">{users.length}</p>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">Institution Users</h3>
              <Button size="sm" onClick={() => setAddUserModal(true)} className="bg-purple-500 hover:bg-purple-600">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            {loadingUsers ? (
              <div className="text-center py-8 text-slate-500">Loading users...</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Role</th>
                      <th className="text-left py-3 px-4 font-medium">Department</th>
                      <th className="text-right py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className="border-t hover:bg-purple-50 cursor-pointer transition-colors"
                        onClick={() => setUserDetailView({ open: true, user })}
                      >
                        <td className="py-3 px-4 font-medium">{user.full_name}</td>
                        <td className="py-3 px-4">{user.email}</td>
                        <td className="py-3 px-4">
                          <span className="capitalize">{user.role.replace('_', ' ')}</span>
                        </td>
                        <td className="py-3 px-4">{user.department || '-'}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" onClick={() => setChangePasswordModal({ open: true, user })}>
                              <Key className="w-3 h-3 mr-1" />
                              Password
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => handleDeleteUser(user.id, user.full_name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-500">
                          No users found for this institution
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={addUserModal} onOpenChange={setAddUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user for {institution?.institution_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={newUserData.full_name} onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})} placeholder="Enter full name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newUserData.email} onChange={(e) => setNewUserData({...newUserData, email: e.target.value})} placeholder="user@example.com" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={newUserData.password} onChange={(e) => setNewUserData({...newUserData, password: e.target.value})} placeholder="Min 8 characters, letters + numbers" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role *</Label>
                <select value={newUserData.role} onChange={(e) => setNewUserData({...newUserData, role: e.target.value})} className="w-full h-10 px-3 rounded-md border">
                  <option value="super_admin">Super Admin (Institution)</option>
                  <option value="admin">Admin</option>
                  <option value="doctor">Doctor</option>
                  <option value="nurse">Nurse</option>
                  <option value="receptionist">Receptionist</option>
                  <option value="lab_technician">Lab Technician</option>
                  <option value="imaging_technician">Imaging Technician</option>
                  <option value="pharmacist">Pharmacist</option>
                  <option value="stockist">Stockist</option>
                </select>
              </div>
              <div>
                <Label>Department {['doctor','nurse','lab_technician','imaging_technician','pharmacist'].includes(newUserData.role) ? '*' : ''}</Label>
                {['doctor','nurse','lab_technician','imaging_technician','pharmacist','stockist'].includes(newUserData.role) ? (
                  <select value={newUserData.department} onChange={(e) => setNewUserData({...newUserData, department: e.target.value})} className="w-full h-10 px-3 rounded-md border">
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                  </select>
                ) : (
                  <Input value={newUserData.department} onChange={(e) => setNewUserData({...newUserData, department: e.target.value})} placeholder="e.g., Administration" />
                )}
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={newUserData.phone} onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})} placeholder="+91 XXXXXXXXXX" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserModal(false)}>Cancel</Button>
            <Button onClick={handleAddUser} className="bg-purple-500 hover:bg-purple-600">Add User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={changePasswordModal.open} onOpenChange={(open) => setChangePasswordModal({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Set new password for {changePasswordModal.user?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>New Password *</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters, letters + numbers" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordModal({ open: false, user: null })}>Cancel</Button>
            <Button onClick={handleChangePassword} className="bg-red-500 hover:bg-red-600">Change Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail View */}
      <Dialog open={userDetailView.open} onOpenChange={(open) => setUserDetailView({ open, user: null })}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none">
          <DialogHeader className="px-8 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => setUserDetailView({ open: false, user: null })}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <DialogTitle className="text-2xl">User Details</DialogTitle>
                  <DialogDescription>Manage user information and credentials</DialogDescription>
                </div>
              </div>
            </div>
          </DialogHeader>
          {userDetailView.user && (
            <div className="space-y-6 mt-6 px-8 pb-8 max-w-6xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    User Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-500">Full Name</p>
                      <p className="text-lg font-semibold text-slate-900">{userDetailView.user.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="text-lg font-medium text-slate-900">{userDetailView.user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Role</p>
                      <p className="text-lg font-medium capitalize">{userDetailView.user.role.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Department</p>
                      <p className="text-lg font-medium">{userDetailView.user.department || 'Not assigned'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="text-lg font-medium">{userDetailView.user.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      <p className="text-lg font-medium">{userDetailView.user.is_active !== false ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => {
                      setChangePasswordModal({ open: true, user: userDetailView.user });
                      setUserDetailView({ open: false, user: null });
                    }}>
                      <Key className="w-4 h-4 mr-2" />
                      Change Password
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        handleDeleteUser(userDetailView.user.id, userDetailView.user.full_name);
                        setUserDetailView({ open: false, user: null });
                      }}
                    >
                      Delete User
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <div className="flex justify-between items-center pt-4">
                <Button variant="outline" onClick={() => setUserDetailView({ open: false, user: null })}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to User List
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
